'use client';

import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslations } from 'next-intl';
import type {
  BodyScrollEvent,
  CellContextMenuEvent,
  ColDef,
  GetRowIdParams,
  GridApi,
  GridReadyEvent,
  RowClassParams,
  RowClickedEvent,
  RowStyle,
  SortChangedEvent,
} from 'ag-grid-community';
import { Box, HStack, token } from '@terra/ui-styled-system';
import {
  FINDINGS_GROUP_HEADER_ROW_CLASS,
  MetaDefault,
  Spinner,
  TABLE_IDS,
  Table,
  archivedEntityRowCss,
  isArchivedLifecycle,
  isFindingsGroupHeaderRow,
  type FindingsGridRow,
  type InternalEnrichedFinding,
} from '@terra/ui-design-system';
import { Alert01Icon } from '@terra/ui-icons';
import type { FindingsGroupByField } from '@terra/shared-contracts-findings';
import { useDomScrollPreservation } from '../../../hooks/useDomScrollPreservation';
import { useFeatureFlags } from '../../../hooks/useFeatureFlags';
import { useMainContentTabs } from '../../../store/layoutStore';
import { useTargetId } from '../../../store/projectStore';
import { EmptyState } from '../../EmptyState';
import {
  LIST_PAGE_TABLE_HEADER_CSS,
  LIST_PAGE_TABLE_HEADER_HEIGHT_PX,
  LIST_PAGE_TABLE_THEME_PARAMS,
  createGroupHeaderStickyClassName,
} from '../../common/list-page-table-theme';
import { ListPageTableBodyLayer } from '../../common/ListPageTableBodyLayer';
import { useListPageTablePersistence } from '../../common/useListPageTablePersistence';
import {
  resolveFindingsColumnSortState,
  resolveFindingsSortChange,
  type FindingsColumnSort,
  type FindingsGroupSortOrder,
  type FindingsSortState,
} from './findings-list.model';
import { useFindingsTableColumnDefs } from './useFindingsTableColumnDefs';
import { isRowClickDisabledTarget } from '../rowClickBlocker';

const FINDINGS_TABLE_ROW_HEIGHT_PX = 36;
const findingsGroupHeaderStickyClassName = createGroupHeaderStickyClassName({
  rowClass: FINDINGS_GROUP_HEADER_ROW_CLASS,
  backgroundColor: 'bg.subtle',
});

const FINDINGS_TABLE_LOAD_MORE_THRESHOLD_ROWS = 20;
const USER_SORT_SOURCE = 'uiColumnSorted';
const FINDINGS_PENDING_SEVERITY_CHANGE_ROW_CLASS =
  'findings-pending-severity-change-row';
const DEFAULT_COL_DEF: ColDef<FindingsGridRow> = {
  sortable: false,
  filter: false,
  resizable: true,
  lockPinned: true,
  sortingOrder: ['asc', 'desc'],
};

type FindingsTableProps = {
  rows: FindingsGridRow[];
  loadMoreRowIndex: number | null;
  findingCount: number;
  groupByField: FindingsGroupByField | null;
  groupSortOrder: FindingsGroupSortOrder;
  columnSort: FindingsColumnSort;
  isLoading: boolean;
  isFetchingMore: boolean;
  hasMore: boolean;
  emptyDescription: string;
  onLoadMore: () => void;
  onSortStateChange: (
    change: Pick<FindingsSortState, 'columnSort' | 'groupSortOrder'>,
  ) => void;
  onOpenFinding: (finding: InternalEnrichedFinding) => void;
  onOpenFindingContextMenu: ({
    event,
    finding,
  }: {
    event: MouseEvent;
    finding: InternalEnrichedFinding;
  }) => void;
};

const getFindingsTableRowId = (params: GetRowIdParams<FindingsGridRow>) =>
  params.data.id;

const getFindingsTableRowStyle = (
  params: RowClassParams<FindingsGridRow>,
): RowStyle => {
  const lifecycle = isFindingsGroupHeaderRow(params.data)
    ? null
    : params.data?.lifecycle;

  return {
    cursor: 'pointer',
    ...(isArchivedLifecycle(lifecycle) ? archivedEntityRowCss : {}),
  };
};

const getFindingsTableRowClass = ({
  params,
  isSeverityChangeRequestEnabled,
}: {
  params: RowClassParams<FindingsGridRow>;
  isSeverityChangeRequestEnabled: boolean;
}) => {
  if (isFindingsGroupHeaderRow(params.data)) {
    return FINDINGS_GROUP_HEADER_ROW_CLASS;
  }

  if (isSeverityChangeRequestEnabled && params.data?.pendingProposedSeverity) {
    return FINDINGS_PENDING_SEVERITY_CHANGE_ROW_CLASS;
  }

  return undefined;
};

function FindingsTableEmptyState({
  emptyDescription,
}: Pick<FindingsTableProps, 'emptyDescription'>) {
  const t = useTranslations('proxy.findings.table');

  return (
    <EmptyState
      icon={Alert01Icon}
      title={t('emptyTitle')}
      description={emptyDescription}
    />
  );
}

export function FindingsTable({
  rows,
  loadMoreRowIndex,
  findingCount,
  groupByField,
  groupSortOrder,
  columnSort,
  isLoading,
  isFetchingMore,
  hasMore,
  emptyDescription,
  onLoadMore,
  onSortStateChange,
  onOpenFinding,
  onOpenFindingContextMenu,
}: FindingsTableProps) {
  const t = useTranslations('proxy.findings.table');
  const { isFeatureFlagEnabled: isSeverityChangeRequestEnabled } =
    useFeatureFlags('SEVERITY_CHANGE_REQUEST_ENABLED');
  const columnDefs = useFindingsTableColumnDefs({ groupByField });
  const persistence = useListPageTablePersistence(
    TABLE_IDS.PROXY_FINDINGS_LIST,
  );
  const containerRef = useRef<HTMLDivElement | null>(null);
  const gridApiRef = useRef<GridApi<FindingsGridRow> | null>(null);
  const resolveRowClass = useCallback(
    (params: RowClassParams<FindingsGridRow>) =>
      getFindingsTableRowClass({
        params,
        isSeverityChangeRequestEnabled,
      }),
    [isSeverityChangeRequestEnabled],
  );
  const tableCss = useMemo(
    () => ({
      ...(isSeverityChangeRequestEnabled
        ? {
            [`& .${FINDINGS_PENDING_SEVERITY_CHANGE_ROW_CLASS} .ag-cell`]: {
              backgroundColor: token('colors.bg.severity.high'),
            },
            '& .ag-cell[col-id="severity"]': {
              overflow: 'visible',
            },
          }
        : {}),
      ...LIST_PAGE_TABLE_HEADER_CSS,
    }),
    [isSeverityChangeRequestEnabled],
  );
  const targetId = useTargetId();
  const { activePageScrollKey } = useMainContentTabs();
  const scrollId =
    activePageScrollKey !== undefined
      ? `${activePageScrollKey}:findings-table`
      : `findings-table-${targetId ?? 'unknown'}`;
  const { scrollerRef } = useDomScrollPreservation({
    scrollId,
    isRestoreReady: !isLoading || findingCount > 0,
    contentRevision: rows.length,
  });
  const loadMoreIfNearEnd = useCallback(
    (api: GridApi<FindingsGridRow>) => {
      if (!hasMore || isFetchingMore || loadMoreRowIndex === null) {
        return;
      }

      if (
        api.getLastDisplayedRowIndex() >=
        loadMoreRowIndex - FINDINGS_TABLE_LOAD_MORE_THRESHOLD_ROWS
      ) {
        onLoadMore();
      }
    },
    [hasMore, isFetchingMore, loadMoreRowIndex, onLoadMore],
  );
  const columnSortState = useMemo(
    () =>
      resolveFindingsColumnSortState({
        columnSort,
        groupByField,
        groupSortOrder,
      }),
    [columnSort, groupByField, groupSortOrder],
  );
  const applyColumnSortState = useCallback(
    (api: GridApi<FindingsGridRow>) =>
      api.applyColumnState({
        state: columnSortState,
        defaultState: { sort: null, sortIndex: null },
      }),
    [columnSortState],
  );

  useEffect(() => {
    if (gridApiRef.current && !gridApiRef.current.isDestroyed()) {
      applyColumnSortState(gridApiRef.current);
    }
  }, [applyColumnSortState]);

  const handleGridReady = useCallback(
    (event: GridReadyEvent<FindingsGridRow>) => {
      gridApiRef.current = event.api;
      applyColumnSortState(event.api);
      scrollerRef(
        containerRef.current?.querySelector<HTMLElement>('.ag-body-viewport') ??
          null,
      );
    },
    [applyColumnSortState, scrollerRef],
  );
  const handleSortChanged = useCallback(
    (event: SortChangedEvent<FindingsGridRow>) => {
      if (event.source !== USER_SORT_SOURCE) {
        return;
      }

      const sortedColumn = event.api
        .getColumnState()
        .find((column) => column.sort === 'asc' || column.sort === 'desc');
      const change = resolveFindingsSortChange({
        sortedColumn: sortedColumn
          ? {
              colId: sortedColumn.colId,
              sort: sortedColumn.sort === 'asc' ? 'asc' : 'desc',
            }
          : null,
        columnSort,
        groupByField,
        groupSortOrder,
      });

      if (
        change.groupSortOrder === groupSortOrder &&
        change.columnSort.colId === columnSort.colId &&
        change.columnSort.sort === columnSort.sort
      ) {
        applyColumnSortState(event.api);

        return;
      }

      onSortStateChange(change);
    },
    [
      applyColumnSortState,
      columnSort,
      groupByField,
      groupSortOrder,
      onSortStateChange,
    ],
  );
  const handleRowClicked = useCallback(
    (event: RowClickedEvent<FindingsGridRow>) => {
      if (
        !event.data ||
        isFindingsGroupHeaderRow(event.data) ||
        isRowClickDisabledTarget(event.event)
      ) {
        return;
      }

      onOpenFinding(event.data);
    },
    [onOpenFinding],
  );
  const handleCellContextMenu = useCallback(
    (event: CellContextMenuEvent<FindingsGridRow>) => {
      if (
        !event.data ||
        isFindingsGroupHeaderRow(event.data) ||
        !(event.event instanceof MouseEvent)
      ) {
        return;
      }

      onOpenFindingContextMenu({ event: event.event, finding: event.data });
    },
    [onOpenFindingContextMenu],
  );

  return (
    <Box
      ref={containerRef}
      flex={1}
      w="full"
      h="full"
      overflow="hidden"
      position="relative"
      className={findingsGroupHeaderStickyClassName}
      css={tableCss}
    >
      <Table
        key={groupByField ?? 'flat'}
        columnDefs={columnDefs}
        persistence={persistence}
        rowData={rows}
        getRowId={getFindingsTableRowId}
        rowHeight={FINDINGS_TABLE_ROW_HEIGHT_PX}
        headerHeight={LIST_PAGE_TABLE_HEADER_HEIGHT_PX}
        noBorder={true}
        fontSize={12}
        headerFontSize={12}
        themeParams={LIST_PAGE_TABLE_THEME_PARAMS}
        animateRows={false}
        defaultColDef={DEFAULT_COL_DEF}
        suppressCellFocus={true}
        suppressMultiSort={true}
        hideColumnControls={true}
        fitColumnsOnGridSizeChanged={false}
        onGridReady={handleGridReady}
        onSortChanged={handleSortChanged}
        onBodyScroll={(event: BodyScrollEvent<FindingsGridRow>) =>
          loadMoreIfNearEnd(event.api)
        }
        onRowDataUpdated={(event) => loadMoreIfNearEnd(event.api)}
        onRowClicked={handleRowClicked}
        onCellContextMenu={handleCellContextMenu}
        preventDefaultOnContextMenu
        getRowStyle={getFindingsTableRowStyle}
        getRowClass={resolveRowClass}
        suppressNoRowsOverlay
      />
      {findingCount === 0 && !isLoading ? (
        <ListPageTableBodyLayer>
          <FindingsTableEmptyState emptyDescription={emptyDescription} />
        </ListPageTableBodyLayer>
      ) : null}
      {isFetchingMore ? (
        <HStack
          position="absolute"
          bottom={3}
          left="50%"
          transform="translateX(-50%)"
          gap={2}
          px={3}
          py={1.5}
          borderRadius="full"
          bg="bg.default"
          boxShadow="sm"
          pointerEvents="none"
        >
          <Spinner size="sm" />
          <MetaDefault color="fg.muted">{t('loadingMore')}</MetaDefault>
        </HStack>
      ) : null}
    </Box>
  );
}
